import { useNavigate } from 'react-router-dom'
import { ConfirmationEventDetails } from 'src/modules/confirmation/components/confirmation-event-details'
import { Button, FormattedText, Icon, Typography } from 'src/modules/core/components'
import { routeNames } from 'src/modules/core/constants/routeNames'
import { useCanReschedule } from 'src/modules/reschedule/hooks/use-can-reschedule'
import type { ConfirmationFormProps } from './confirmation-form.interface'
import classes from './confirmation-form.module.scss'

function shouldHideRescheduleReason(reason: string | undefined): boolean {
  return reason === 'no_booking_page' || reason === 'rescheduling_not_enabled'
}

export function ConfirmationForm({
  event,
  onConfirm,
  onCancel,
  urlCode,
  submittingAction = null,
}: ConfirmationFormProps) {
  const navigate = useNavigate()
  const canRescheduleQuery = useCanReschedule(urlCode ?? '')
  const canRescheduleData = canRescheduleQuery.data

  if (!event) {
    return null
  }

  const isSubmitting = submittingAction !== null

  return (
    <div>
      <Typography variant="h1" className={classes.title}>
        <FormattedText id="confirmation.title" />
      </Typography>
      <ConfirmationEventDetails event={event} />
      <div className={classes.buttonsWrapper}>
        {event.confirmed === 'confirmed' ? (
          <div className={classes.confirmationMessageWrapper}>
            <div className={classes.confirmationSuccessMark}>
              <Icon name="checkmark" />
            </div>
            <Typography variant="body" className={classes.confirmationSuccessText}>
              <FormattedText id="confirmation.alreadyConfirmed" />
            </Typography>
          </div>
        ) : (
          <Button
            onClick={onConfirm}
            disabled={isSubmitting}
            isLoading={submittingAction === 'confirm'}
            loadingLabel={<FormattedText id="confirmation.button.confirming" />}
          >
            <FormattedText id="confirmation.button.confirm" />
          </Button>
        )}
        {urlCode &&
          canRescheduleData &&
          !shouldHideRescheduleReason(canRescheduleData.reason) &&
          (canRescheduleData.canReschedule ? (
            <Button
              variant="withBorder"
              className={classes.rescheduleButton}
              disabled={isSubmitting}
              onClick={() => navigate(routeNames.reschedule.path.replace(':urlCode', urlCode))}
            >
              <FormattedText id="confirmation.button.reschedule" />
            </Button>
          ) : canRescheduleData.reason === 'minimum_notice' ||
            canRescheduleData.reason === 'booking_window' ||
            canRescheduleData.reason === 'multiple_attendees' ||
            canRescheduleData.reason === 'policy_window' ||
            canRescheduleData.reason === 'other' ? (
            <div className={classes.rescheduleDisabled}>
              <FormattedText id="confirmation.cannotReschedule" />
              {canRescheduleData.reason === 'minimum_notice' && (
                <span className={classes.rescheduleDisabledSubtitle}>
                  <FormattedText id="confirmation.appointmentStartsSoon" />
                </span>
              )}
              {canRescheduleData.reason === 'booking_window' && (
                <span className={classes.rescheduleDisabledSubtitle}>
                  <FormattedText id="confirmation.outsideBookingWindow" />
                </span>
              )}
              {canRescheduleData.reason === 'multiple_attendees' && (
                <span className={classes.rescheduleDisabledSubtitle}>
                  <FormattedText id="confirmation.multipleAttendees" />
                </span>
              )}
              {canRescheduleData.reason === 'policy_window' && (
                <span className={classes.rescheduleDisabledSubtitle}>
                  <FormattedText id="confirmation.insideCancellationWindow" />
                </span>
              )}
            </div>
          ) : null)}
        <Button
          variant="danger"
          className={classes.cancelButton}
          onClick={onCancel}
          disabled={isSubmitting}
          isLoading={submittingAction === 'cancel'}
          loadingLabel={<FormattedText id="confirmation.button.cancelling" />}
        >
          <FormattedText id="confirmation.button.cancel" />
        </Button>
      </div>
    </div>
  )
}
